import { ChevronDownIcon, LogOutIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { useLogoutMutation } from '@/modules/auth/mutations/useLogoutMutation';
import { permissions } from '@/modules/auth/permissions';
import { Notifications } from '@/modules/notifications/components/Notifications';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';
import type { CurrentUserApi } from '@/modules/users/types';
import { getInitials } from '@/utils/getInitials';

interface AppHeaderProps {
  signedInUser: CurrentUserApi | undefined;
}

export const AppHeader = ({ signedInUser }: AppHeaderProps) => {
  const navigate = useNavigate();
  const logoutMutation = useLogoutMutation();

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate('/login', { replace: true });
      },
    });
  };

  const initials = signedInUser
    ? getInitials(
        `${signedInUser.firstName ?? ''} ${signedInUser.lastName ?? ''}`,
      )
    : '';

  return (
    <header className="sticky flex shrink-0 items-center justify-end bg-white px-8 py-4 shadow-[2px_1px_5px_0_rgba(0,0,0,0.15)]">
      <div className="flex items-center gap-4">
        <Notifications />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex cursor-pointer items-center gap-1">
              <Avatar className="size-6">
                <AvatarFallback className="text-xs">{initials}</AvatarFallback>
              </Avatar>
              <ChevronDownIcon size={16} className="text-primary-700" />
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="p-2 font-normal">
              My Account
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="p-2"
              onSelect={() => navigate(`/users/${signedInUser?.id}/edit`)}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Avatar className="size-6">
                    <AvatarFallback className="text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  Profile
                </div>
              </div>
            </DropdownMenuItem>

            <PermissionsGate
              permissions={[permissions.UserExternalAccounts.Read]}
            >
              <DropdownMenuItem
                className="p-2"
                onSelect={() =>
                  navigate(`/users/${signedInUser?.id}/external-accounts`)
                }
              >
                <div className="flex w-full items-center justify-between">
                  External Accounts
                </div>
              </DropdownMenuItem>
            </PermissionsGate>

            <DropdownMenuSeparator />

            <DropdownMenuItem className="p-2" onClick={handleLogout}>
              <div className="flex items-center gap-2">
                <LogOutIcon size={16} />
                <span>Logout</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
